import { Col, type ColProps, Descriptions, Divider } from 'antd';
import { useFormikContext } from 'formik';
import { get, isEqual } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MaybankEnvironment } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';

const defaultColSpan = { lg: 8, md: 12, xs: 24 };

export type MaybankIntegrationProps = {
    prefix?: string;
    colSpan?: ColProps;
    isUpdate: boolean;
    disabled?: boolean;
};

const fieldNames = [
    'allowedIps',
    'dealerId',
    'host',
    'clientId',
    'apiKey',
    'environment',
    'clientSecret',
    'encryptionPrivateKey',
    'encryptionPublicCert',
    'signaturePrivateKey',
    'signaturePublicCert',
    'generatedId',
    'generatedSecrets',
    'dealerCode',
] as const;

const useMaybankEnvironmentOptions = () => {
    const { t } = useTranslation(['bankDetails']);

    return useMemo(
        () => [
            {
                value: MaybankEnvironment.Sit,
                label: t('bankDetails:options.maybankIntegration.sit'),
            },
            {
                value: MaybankEnvironment.Uat,
                label: t('bankDetails:options.maybankIntegration.uat'),
            },
            {
                value: MaybankEnvironment.Staging,
                label: t('bankDetails:options.maybankIntegration.staging'),
            },
            {
                value: MaybankEnvironment.Production,
                label: t('bankDetails:options.maybankIntegration.production'),
            },
        ],
        [t]
    );
};

const MaybankIntegration = ({
    prefix,
    colSpan = defaultColSpan,
    isUpdate,
    disabled = false,
}: MaybankIntegrationProps) => {
    const { t } = useTranslation(['bankDetails']);

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    const { values } = useFormikContext();

    const environmentOptions = useMaybankEnvironmentOptions();

    const column = useMemo(() => (isEqual(colSpan, defaultColSpan) ? { xs: 1, md: 2 } : { xs: 1, md: 1 }), [colSpan]);

    return (
        <>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.allowedIps', { returnObjects: true })}
                    disabled={disabled}
                    name={names.allowedIps}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.maybankIntegration.dealerId', { returnObjects: true })}
                    disabled={disabled}
                    name={names.dealerId}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.maybankIntegration.host', { returnObjects: true })}
                    addonBefore="https://"
                    disabled={disabled}
                    name={names.host}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.maybankIntegration.clientId', { returnObjects: true })}
                    disabled={disabled}
                    name={names.clientId}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <PasswordField
                    {...t('bankDetails:fields.maybankIntegration.clientSecret', { returnObjects: true })}
                    disabled={disabled}
                    name={names.clientSecret}
                    placeholder="•••••"
                    required={!isUpdate}
                />
            </Col>
            <Col {...colSpan}>
                <PasswordField
                    {...t('bankDetails:fields.maybankIntegration.apiKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.apiKey}
                    placeholder="•••••"
                    required={!isUpdate}
                />
            </Col>
            <Col {...colSpan}>
                <SelectField
                    {...t('bankDetails:fields.maybankIntegration.environment', { returnObjects: true })}
                    disabled={disabled}
                    name={names.environment}
                    options={environmentOptions}
                    required
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.maybankIntegration.encryptionPrivateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.encryptionPrivateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.maybankIntegration.encryptionPublicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.encryptionPublicCert}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.maybankIntegration.signaturePrivateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.signaturePrivateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.maybankIntegration.signaturePublicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.signaturePublicCert}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.dealerCodeForAudiAndSDM', { returnObjects: true })}
                    disabled={disabled}
                    name={names.dealerCode}
                />
            </Col>
            {isUpdate && (
                <Col xs={24}>
                    <Divider />
                    <Descriptions column={column} bordered>
                        <Descriptions.Item label={t('bankDetails:fields.maybankIntegration.generatedId.label')}>
                            {get(names.generatedId, values)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('bankDetails:fields.maybankIntegration.generatedSecrets.label')}>
                            {get(names.generatedSecrets, values)}
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
            )}
        </>
    );
};

export default MaybankIntegration;
